import React, { useState, useContext, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createPlaceholder, getCaseFolders } from '../../../../../services/api';
import ThemeContext from '../../../../../ThemeContext';

const AddPlaceholderModal = ({ show, onHide, folderId, caseId, onSuccess }) => {
  const { theme } = useContext(ThemeContext);
  const [placeholderName, setPlaceholderName] = useState('');
  const [placeholderContent, setPlaceholderContent] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(folderId || '');
  const [availableFolders, setAvailableFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all folders for this case
  useEffect(() => {
    const fetchFolders = async () => {
      if (show && caseId) {
        try {
          const response = await getCaseFolders(caseId);
          setAvailableFolders(response.data);
          if (!selectedFolderId && response.data.length > 0) {
            setSelectedFolderId(response.data[0].id);
          }
        } catch (error) {
          console.error('Failed to fetch folders:', error);
          toast.error('Failed to fetch folders', {
            style: {
              backgroundColor: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              color: theme.colors.danger,
            },
          });
        }
      }
    };
    fetchFolders();
  }, [show, caseId, theme]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!placeholderName.trim()) {
      toast.warning('Please enter a placeholder name', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }

    if (!selectedFolderId) {
      toast.warning('Please select a folder', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }

    try {
      setIsLoading(true);
      await createPlaceholder(selectedFolderId, {
        name: placeholderName,
        content: { text: placeholderContent },
      });
      toast.success('Placeholder created successfully', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
        },
      });
      setPlaceholderName('');
      setPlaceholderContent('');
      onSuccess();
      onHide();
    } catch (error) {
      toast.error(`Failed to create placeholder: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPlaceholderName('');
    setPlaceholderContent('');
    setSelectedFolderId(folderId || '');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header
        style={{
          backgroundColor: '#17a2b8',
          color: '#fff',
          borderBottom: 'none',
          padding: '1rem 1.5rem',
        }}
      >
        <Modal.Title style={{ fontSize: '16px', fontWeight: '600' }}>
          Placeholder for Exhibit
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          backgroundColor: '#fff',
          color: '#333',
          padding: '1.5rem',
        }}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="placeholderContent"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '12px',
                fontWeight: '500',
                color: '#17a2b8',
              }}
            >
              Description
            </label>
            <textarea
              id="placeholderContent"
              value={placeholderContent}
              onChange={(e) => setPlaceholderContent(e.target.value)}
              placeholder="Enter description..."
              rows={5}
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#333',
                resize: 'vertical',
              }}
              disabled={isLoading}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="placeholderName"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '12px',
                fontWeight: '500',
                color: '#17a2b8',
              }}
            >
              Save As
            </label>
            <input
              type="text"
              id="placeholderName"
              value={placeholderName}
              onChange={(e) => setPlaceholderName(e.target.value)}
              placeholder="Enter placeholder name"
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#333',
              }}
              disabled={isLoading}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="folderSelect"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '12px',
                fontWeight: '500',
                color: '#17a2b8',
              }}
            >
              Save in Folder
            </label>
            <select
              id="folderSelect"
              value={selectedFolderId}
              onChange={(e) => setSelectedFolderId(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#333',
              }}
              disabled={isLoading || availableFolders.length === 0}
            >
              {availableFolders.length === 0 ? (
                <option value="">No folders available</option>
              ) : (
                availableFolders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer
        style={{
          backgroundColor: '#fff',
          borderTop: '1px solid #ddd',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
        }}
      >
        <button
          onClick={handleClose}
          disabled={isLoading}
          style={{
            padding: '8px 24px',
            fontSize: '12px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#dc3545',
            color: '#fff',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
            fontWeight: '500',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !placeholderName.trim() || !selectedFolderId}
          style={{
            padding: '8px 24px',
            fontSize: '12px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#17a2b8',
            color: '#fff',
            cursor: isLoading || !placeholderName.trim() || !selectedFolderId ? 'not-allowed' : 'pointer',
            opacity: isLoading || !placeholderName.trim() || !selectedFolderId ? 0.6 : 1,
            fontWeight: '500',
          }}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddPlaceholderModal;